import {
  DeviceCapabilities,
  SensorCapabilities,
  SiteCapabilities,
  StudyCapabilities,
  SubjectCapabilities,
} from 'adc-sirius-module-common';
import { OrganizationCapabilities } from 'adc-sirius-module-common';
import { queryOptions, UseQueryOptions } from '@tanstack/react-query';
import { ApiErrorResponse } from '../models';
import mediator from '../utilities/mediator';
import log from '../utilities/log';

type CapabilitiesQueryParams = {
  key?: string;
};

type DeviceCollectionCapabilities = {
  canGetDevices: boolean;
  canManageDeviceAssignments: boolean;
};

export type StudyViewCapabilities = StudyCapabilities &
  DeviceCollectionCapabilities;
export type SiteViewCapabilities = SiteCapabilities &
  DeviceCollectionCapabilities;
export type SubjectViewCapabilities = SubjectCapabilities &
  DeviceCollectionCapabilities;

export function organizationCapabilitiesQueryOptions(
  params: CapabilitiesQueryParams,
  options?: Partial<UseQueryOptions<OrganizationCapabilities>>,
) {
  return queryOptions<OrganizationCapabilities>({
    ...options,
    queryKey: ['capabilityOrganization', params.key],
    queryFn: async () => {
      try {
        return await mediator.request<OrganizationCapabilities>(
          'capability:getOrganizationCapabilities',
          { ...params },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key && (options?.enabled ?? true),
  });
}

export function studyCapabilitiesQueryOptions(
  params: CapabilitiesQueryParams,
  options?: Partial<UseQueryOptions<StudyViewCapabilities>>,
) {
  return queryOptions<StudyViewCapabilities>({
    ...options,
    queryKey: ['capabilityStudy', params.key],
    queryFn: async () => {
      try {
        return await mediator.request<StudyViewCapabilities>(
          'capability:getStudyCapabilities',
          { ...params },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key && (options?.enabled ?? true),
  });
}

export function siteCapabilitiesQueryOptions(
  params: CapabilitiesQueryParams,
  options?: Partial<UseQueryOptions<SiteViewCapabilities>>,
) {
  return queryOptions<SiteViewCapabilities>({
    ...options,
    queryKey: ['capabilitySite', params.key],
    queryFn: async () => {
      try {
        return await mediator.request<SiteViewCapabilities>(
          'capability:getSiteCapabilities',
          { ...params },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key && (options?.enabled ?? true),
  });
}

export function subjectCapabilitiesQueryOptions(
  params: CapabilitiesQueryParams,
  options?: Partial<UseQueryOptions<SubjectViewCapabilities>>,
) {
  return queryOptions<SubjectViewCapabilities>({
    ...options,
    queryKey: ['capabilitySubject', params.key],
    queryFn: async () => {
      try {
        return await mediator.request<SubjectViewCapabilities>(
          'capability:getSubjectCapabilities',
          { ...params },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key && (options?.enabled ?? true),
  });
}

export function deviceCapabilitiesQueryOptions(
  params: CapabilitiesQueryParams,
  options?: Partial<UseQueryOptions<DeviceCapabilities>>,
) {
  return queryOptions<DeviceCapabilities>({
    ...options,
    queryKey: ['capabilityDevice', params.key],
    queryFn: async () => {
      try {
        return await mediator.request<DeviceCapabilities>(
          'capability:getDeviceCapabilities',
          { ...params },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key && (options?.enabled ?? true),
  });
}

export function sensorCapabilitiesQueryOptions(
  params: CapabilitiesQueryParams,
  options?: Partial<UseQueryOptions<SensorCapabilities>>,
) {
  return queryOptions<SensorCapabilities>({
    ...options,
    queryKey: ['capabilitySensor', params.key],
    queryFn: async () => {
      try {
        return await mediator.request<SensorCapabilities>(
          'capability:getSensorCapabilities',
          { ...params },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key && (options?.enabled ?? true),
  });
}
